import React, {useCallback, useEffect, useRef, useState} from 'react';

import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    Typography} from '@mui/material';

import {Dialog as BaseDialog, Text} from 'components';
import {isOk} from 'fetcher/useFetcher';
import {useInterval, useTranslation} from 'hooks';
import {usePairingStore} from 'module/pairing';

// import {useInfoStore} from 'store';
import {usePairingAdd} from './usePairing';

import {DateTime} from 'luxon';
import ReactQRCode from 'qrcode.react';

const styles = {
    dialogTextContainer: {
        background: '#000',
    },
    dialogContainer: {
        align: 'center',
        background: '#000',
    },
    qrcodeContainer: {
        backgroundColor: '#fff', // keep this, otherwise phones have problem scanning the qrcode
        display: 'inline-flex',
        padding: 2
    },
    pairingCode: {
        fontSize: '45px',
        marginTop: '36px',
        marginBottom: '10px',
        letterSpacing: '20px', // increase letter spacing
    },
    buttonContainer: {
        marginTop: 2,
    }
};

const Dialog = ({open, handleClose}) => {
    const translate = useTranslation();
    // const deviceId = useInfoStore(store => store.deviceId);
    // const deviceTitle = useInfoStore(store => store.deviceId);
    const setActivePairing = usePairingStore((state) => state.set);
    const unsetActivePairing = usePairingStore((state) => state.unset);
    const activePairing = usePairingStore((state) => state.activePairing);

    const {add, loading} = usePairingAdd();
    const [code, setCode] = useState('');
    const [codeValidity, setCodeValidity] = useState(activePairing?.valid_until || null);
    const [shouldRefresh, setShouldRefresh] = useState(false);
    
    // Use ref to track current open state for interval callback
    const isOpenRef = useRef(open);
    
    // Track if user successfully paired (has JWT)
    const hasPairedRef = useRef(false);
    
    let refreshTime = DateTime.fromISO(codeValidity).diff(DateTime.now()).milliseconds || 300000;

    // Update ref whenever open changes
    useEffect(() => {
        isOpenRef.current = open;
    }, [open]);

    // Only run the interval if the dialog is open and not connected
    useInterval(useCallback(async () => {
        // TODO add info message based on device type or location
        // for refreshing the pairing code every 5 minutes
        // Use ref to check current open state
        if (!isOpenRef.current || !shouldRefresh || !codeValidity || activePairing.jwt) {
            console.log('[Dialog Interval] Skipping refresh - dialog closed or conditions not met', {
                isOpen: isOpenRef.current,
                shouldRefresh,
                codeValidity: !!codeValidity,
                hasJwt: !!activePairing.jwt
            });
            return;
        }

        console.log('[Dialog Interval] Refreshing pairing code...', refreshTime / 1000);
        try {
            const res = await add('INFO MESSAGE');
            console.log('[Dialog Interval] refresh pairing code successfully submitted', res);
            
            // Check again if dialog is still open before updating state (using ref)
            if (isOk(res) && typeof res == 'object' && isOpenRef.current) {
                console.log('[Dialog Interval] pairing code:', res.code);
                setCodeValidity(res.valid_until);
                setCode(res.code);
                setActivePairing(res);
            } else {
                console.log('[Dialog Interval] Dialog closed during API call, not updating state');
            }
        } catch (error) {
            console.error('[Dialog Interval] Error submitting pairing code', error);
        }
        // new code after 5 minutes * 60 seconds * 1000 -> 300000
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeValidity, activePairing, shouldRefresh]), shouldRefresh && open ? refreshTime : null
    );

    useEffect(() => {
        console.log('[Pairing Dialog] Opening - fetching pairing code');

        const getPairingCode = async () => {
        // for refreshing the pairing code every 5 minutes
            try {
                const res = await add('INFO MESSAGE');
                console.log('initial pairing code successfully submitted', res);
                if (isOk(res) && typeof res == 'object') {
                    console.log('pairing code:', res.code);
                    // console.log('VALID UNTIL', DateTime.fromISO(res.valid_until).diff(DateTime.now()));
                    setCodeValidity(res.valid_until);
                    setCode(res.code);
                    setActivePairing(res);
                    setShouldRefresh(true);

                }
            } catch (error) {
                console.error('Error submitting pairing code', error);
            }
        // handleClose might not be wrapped into useCallback, therefore do not add it as dep
        // eslint-disable-next-line react-hooks/exhaustive-deps
        };

        // TODO maybe check if there is an existing pairing code before getting a new one
        getPairingCode();

        // Cleanup function runs when component unmounts
        return () => {
            console.log('[Pairing Dialog] Component unmounting - hasPaired:', hasPairedRef.current);
            // Only clear active pairing if user did NOT successfully pair
            if (!hasPairedRef.current) {
                console.log('[Pairing Dialog] No successful pairing, clearing active pairing');
                unsetActivePairing();
            } else {
                console.log('[Pairing Dialog] User successfully paired, keeping active pairing');
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Track when user successfully pairs (gets JWT)
    useEffect(() => {
        if (activePairing?.jwt) {
            console.log('[Pairing Dialog] User successfully paired, setting hasPairedRef to true');
            hasPairedRef.current = true;
        }
    }, [activePairing?.jwt]);

    return <BaseDialog open={open} handleClose={handleClose} title={translate('app.pairing.pairing')}>
        {!activePairing.jwt ? (<>
            <DialogContent sx={styles.dialogTextContainer} align={'center'}>
                <Typography variant="body2" color="white.main"><Text>app.pairing.info</Text></Typography>
            </DialogContent>
            <DialogContent sx={styles.dialogContainer} align={'center'}>
                {!loading && code !== '' ? <Box sx={styles.qrcodeContainer}>
                    <ReactQRCode value={`tactix://pairing?code=${code}`} level={'H'}/>
                </Box> : <CircularProgress color="inherit"/>}
                <Typography sx={styles.pairingCode} color="primary" variant="h1"> {code}</Typography>
                {codeValidity && <Timer dateTime={codeValidity} />}
            </DialogContent>
        </>)
            :(<>
                <DialogContent sx={styles.dialogTextContainer} align={'center'}>
                    <Typography color="primary" variant="h2" textTransform="uppercase"><Text>app.pairing.message.success</Text></Typography>
                    <Box sx={styles.buttonContainer}>
                        <Button onClick={handleClose} color="primary" variant="contained"> <Text>app.layout.message.close_window</Text></Button>
                    </Box>
                </DialogContent>
            </>)
        }
        <DialogActions>
        </DialogActions>
    </BaseDialog>;
};

const Timer = ({dateTime}) => {
    const translate = useTranslation();
    const [timeLeft, setTimeLeft] = useState(DateTime.fromISO(dateTime).diff(DateTime.now()));

    useEffect(() => {
        const timerInterval = setInterval(() => {
            setTimeLeft(DateTime.fromISO(dateTime).diff(DateTime.now()));
        }, 1000);

        return () => clearInterval(timerInterval);
    }, [dateTime]);

    return (
    
        <Typography variant="body2" color="primary">{translate('app.pairing.message.code_time_left', timeLeft.toFormat('mm:ss'))}</Typography>
    );};

export default Dialog;